import { inject, Injectable } from '@angular/core';
import * as moment from 'moment';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { Utils } from '@klickdata/core/util';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { OccasionDate, Resource, ResourceCheckKeys } from './resource.model';
import { ResourceTypes } from './types.enum';

/** Approval status values used for enrollment; kept as constants for clarity and debugging. */
const ENROLLMENT_APPROVAL = {
    APPROVED: 'approved',
    PENDING: 'pending',
    WAITING: 'waiting',
    ENROLLED: 'enrolled',
    REJECTED: 'rejected',
} as const;

export interface PlayableResourceSpec {
    isAllowed?: boolean;
    clickable?: boolean;
    label?: string;
    icon?: string;
    checkType?: ResourceCheckKeys;
    desc?: string;
    fg?: string;
    bg?: string;
    border?: string;
    shouldCheckPrice?: boolean;
}

/** Label and icon for the play button. A missing action means the learner has not started yet. */
export function buttonForPlaybackAction(playbackAction?: 'start' | 'repeat' | 'continue'): {
    label: string;
    icon: string;
} {
    if (!playbackAction) {
        return { label: $localize`Start`, icon: 'play_circle' };
    }
    if (playbackAction === 'start') {
        return { label: $localize`Play`, icon: 'play_circle' };
    }
    return { label: $localize`Repeat`, icon: 'repeat' };
}

/** Reusable spec fragments for consistent UI and easier debugging. */
const SPEC_STYLES = {
    paymentNeeded: { fg: '#fff', bg: '#e44a66' },
    approved: { fg: '#3e5365', bg: '#bfd8d0' },
    pending: { fg: '#ff9961', bg: '#fff', border: '1px solid #ff9961' },
    play: { fg: '#fff', bg: '#3e5365' },
    closed: { fg: '#a7a7a7', bg: '#fff' },
    soon: { fg: '#000', bg: '#bfd8d0' },
} as const;

@Injectable({
    providedIn: 'root',
})
export class ResourcePlayabilityService extends OnDestroyHandler {
    private readonly auth = inject(AuthService);

    /**
     * Resolves the final playable state for a resource, including an optional price check.
     * Use this when the UI needs a single observable to enable/disable play and show the right label.
     */
    playResource(resource: Resource, occasionDate?: OccasionDate): Observable<PlayableResourceSpec> {
        return this.checkPlayableResource(resource, occasionDate).pipe(
            switchMap((result) => {
                if (!result.shouldCheckPrice) {
                    return of(result);
                }
                return this.auth.isValid(true).pipe(
                    take(1),
                    switchMap((authenticated) => {
                        if (!authenticated) {
                            return of(result);
                        }
                        return this.auth.getCustomer().pipe(
                            take(1),
                            switchMap((customer) =>
                                this.isPaymentNeeded(resource, customer)
                                    ? of(this.createPaymentNeededSpec())
                                    : of(result)
                            )
                        );
                    })
                );
            })
        );
    }

    /**
     * Determines whether the resource can be played and how it should be presented (label, icon, etc.).
     * Does not perform price check; use playResource() for that.
     */
    checkPlayableResource(resource: Resource, occasionDate?: OccasionDate): Observable<PlayableResourceSpec> {
        return this.getScormErrorSpec(resource).pipe(
            switchMap((spec) => (spec != null ? of(spec) : this.getNoItemsSpec(resource))),
            switchMap((spec) => (spec != null ? of(spec) : this.getOpportunitySpec(resource))),
            // switchMap((spec) => (spec != null ? of(spec) : this.getCompetenceClosedSpec(resource))),
            switchMap((spec) => (spec != null ? of(spec) : this.getEnrollmentStatusSpec(resource, occasionDate))),
            switchMap((spec) => (spec != null ? of(spec) : this.getOpenAlwaysOrOccasionSpec(resource, occasionDate))),
            map((spec) => spec ?? this.createClosedFallbackSpec(resource))
        );
    }

    public isPaymentNeeded(resource: Resource, customer: Customer): boolean {
        return !!(customer && customer.license_type !== 'sitelicense' && resource?.price && !resource?.purchased);
    }

    // --- Private check methods (each handles one concern; returns null when the check does not apply) ---

    private getScormErrorSpec(resource: Resource): Observable<PlayableResourceSpec | null> {
        const isScormInError =
            ResourceTypes.parentType(resource.type_id) === ResourceTypes.SCORM && resource.prompt_status === 'ERROR';
        if (!isScormInError) {
            return of(null);
        }
        const desc = resource.prompt_error
            ? $localize`This SCORM is not ready yet (${resource.prompt_error}).`
            : $localize`You can't play this SCORM.`;
        return of({
            isAllowed: false,
            label: $localize`Can't play SCORM`,
            icon: 'block',
            ...SPEC_STYLES.closed,
            desc,
            checkType: ResourceCheckKeys.OTHER,
        });
    }

    private getNoItemsSpec(resource: Resource): Observable<PlayableResourceSpec | null> {
        const resourceType = ResourceTypes.parentType(resource.type_id);
        const resourceLabel = ResourceTypes.label(resourceType, { capitalize: false });
        const typeRequiresItems = ResourceTypes.isTypeThatHasItems(resourceType);
        const hasNoItems = !resource.number_of_items;
        if (!(hasNoItems && typeRequiresItems)) {
            return of(null);
        }
        return of({
            isAllowed: false,
            label: $localize`Closed`,
            icon: 'block',
            ...SPEC_STYLES.closed,
            desc: $localize`You can't play this ${resourceLabel} because it has no items`,
            checkType: ResourceCheckKeys.ITEMS,
        });
    }

    private getOpportunitySpec(resource: Resource): Observable<PlayableResourceSpec | null> {
        if (resource.playbackAction === 'continue') {
            return of({
                isAllowed: true,
                label: $localize`Continue`,
                icon: 'double_arrow',
                ...SPEC_STYLES.play,
                checkType: ResourceCheckKeys.OPPORTUNITY,
            });
        }
        return of(null);
    }

    private getCompetenceClosedSpec(resource: Resource): Observable<PlayableResourceSpec | null> {
        const competence = resource.user_competence;
        if (!competence) {
            return of(null);
        }
        const expiryDate = competence.expiry_date;
        const isExpired = expiryDate && moment(expiryDate).isBefore(moment());
        const alreadyTaken = !!competence.done_at;
        if (!expiryDate || (isExpired && !alreadyTaken)) {
            return of(null);
        }
        const expiryFormatted = moment(expiryDate).format('YY-MM-DD');
        return of({
            isAllowed: false,
            label: $localize`Closed`,
            icon: 'block',
            ...SPEC_STYLES.closed,
            desc: $localize`You have already taken this resource in this recurring period, you can take it again only after ${expiryFormatted}`,
            checkType: ResourceCheckKeys.MANDATORY,
        });
    }

    private getEnrollmentStatusSpec(
        resource: Resource,
        occasionDate?: OccasionDate
    ): Observable<PlayableResourceSpec | null> {
        const hasApprovalStatus = !!resource.user_enrollment?.approval_status;
        if (!hasApprovalStatus) {
            return of(null);
        }

        if (occasionDate && occasionDate.id !== resource.enrollmentMetadata?.occasion_id) {
            return of({
                isAllowed: false,
                label: $localize`Closed`,
                icon: 'block',
                ...SPEC_STYLES.closed,
                desc: $localize`You have already enrolled in other occasion in this resource`,
                checkType: ResourceCheckKeys.ENROLL,
            });
        }
        if (
            resource.user_enrollment?.approval_status === ENROLLMENT_APPROVAL.APPROVED ||
            resource.user_enrollment?.approval_status === ENROLLMENT_APPROVAL.ENROLLED
        ) {
            const canStart = this.canStartOccasionByDate(resource, occasionDate);
            if (canStart) {
                return of({
                    isAllowed: true,
                    label:
                        resource.user_enrollment?.approval_status === ENROLLMENT_APPROVAL.APPROVED
                            ? $localize`Approved`
                            : $localize`Enrolled`,
                    icon:
                        resource.user_enrollment?.approval_status === ENROLLMENT_APPROVAL.APPROVED
                            ? 'task_alt'
                            : 'app_registration',
                    ...SPEC_STYLES.approved,
                    checkType: ResourceCheckKeys.ENROLL,
                });
            }
            if (this.isBetweenEnrollmentPeriodAndOccasionPeriod(resource, occasionDate)) {
                const nearestOccasionDateLabel = this.getNearestOccasionDateLabel(resource, occasionDate);

                return of({
                    isAllowed: false,
                    label: $localize`Soon`,
                    icon: 'schedule',
                    ...SPEC_STYLES.soon,
                    desc: $localize`The resource is currently not available, But it's coming soon ${
                        nearestOccasionDateLabel ? `on ${nearestOccasionDateLabel}` : ''
                    }. Stay tuned!`,
                    checkType: ResourceCheckKeys.ENROLL,
                    shouldCheckPrice: true,
                });
            }
            if (this.isOccasionPeriodPassed(resource, occasionDate)) {
                return of({
                    isAllowed: false,
                    label: $localize`Passed`,
                    icon: 'block',
                    ...SPEC_STYLES.closed,
                    desc: $localize`The resource is currently not available, But it's coming soon. Stay tuned!`,
                });
            }
        }

        switch (resource.user_enrollment?.approval_status) {
            case ENROLLMENT_APPROVAL.APPROVED:
                return of({
                    isAllowed: true,
                    label: $localize`Approved`,
                    icon: 'task_alt',
                    ...SPEC_STYLES.approved,
                    checkType: ResourceCheckKeys.ENROLL,
                });
            case ENROLLMENT_APPROVAL.PENDING:
                return of({
                    isAllowed: false,
                    label: $localize`Pending`,
                    icon: 'pending_actions',
                    ...SPEC_STYLES.pending,
                    checkType: ResourceCheckKeys.ENROLL,
                });
            case ENROLLMENT_APPROVAL.WAITING:
                return of({
                    isAllowed: false,
                    label: $localize`Waiting`,
                    icon: 'pending',
                    ...SPEC_STYLES.pending,
                    checkType: ResourceCheckKeys.ENROLL,
                });
            case ENROLLMENT_APPROVAL.ENROLLED:
                return of({
                    isAllowed: false,
                    label: $localize`Enrolled`,
                    icon: 'pending_actions',
                    ...SPEC_STYLES.pending,
                    checkType: ResourceCheckKeys.ENROLL,
                });
            case ENROLLMENT_APPROVAL.REJECTED:
                return of({
                    isAllowed: false,
                    label: $localize`Rejected`,
                    icon: 'block',
                    ...SPEC_STYLES.paymentNeeded,
                    checkType: ResourceCheckKeys.ENROLL,
                });
            default:
                return of(null);
        }
    }

    private getOpenAlwaysOrOccasionSpec(
        resource: Resource,
        occasionDate?: OccasionDate
    ): Observable<PlayableResourceSpec | null> {
        const metadata = resource.enrollmentRequiredMetadata;
        if (!metadata) {
            return of(null);
        }

        if (metadata.occasion_always_open && !resource.enrollmentRequiredStatus) {
            return of(this.createClosedFallbackSpec(resource));
        }

        const occasionDates = metadata.occasion_dates?.filter((occ) => !occ.disabled) ?? [];
        if (occasionDates.length === 0 && !resource.enrollmentRequiredStatus) {
            return of(null);
        }

        const canEnroll = this.canEnrollInOccasion(resource, occasionDate);
        const canStart = this.canStartOccasionByDate(resource, occasionDate);

        if (canEnroll) {
            return of({
                isAllowed: true,
                label: $localize`Enroll`,
                icon: 'app_registration',
                ...SPEC_STYLES.play,
                checkType: ResourceCheckKeys.ENROLL,
                shouldCheckPrice: true,
            });
        }
        if (canStart) {
            return of(this.createClosedFallbackSpec(resource));
        }
        return of(this.getBlockedOccasionSpec(resource, occasionDate));
    }

    public canEnrollInOccasion(resource: Resource, occasionDate?: OccasionDate): boolean {
        if (occasionDate) {
            return (
                Utils.isValidToEnrollOccasionByDate(occasionDate) &&
                Utils.isValidToEnrollOccasionBySeats(occasionDate, resource.enrollmentRequiredStatus)
            );
        }
        return (resource.enrollmentRequiredMetadata?.occasion_dates?.filter((occ) => !occ.disabled) ?? []).some(
            (occasion) =>
                Utils.isValidToEnrollOccasionByDate(occasion) &&
                Utils.isValidToEnrollOccasionBySeats(occasion, resource.enrollmentRequiredStatus)
        );
    }

    public canStartOccasionByDate(resource: Resource, occasionDate?: OccasionDate): boolean {
        if (occasionDate) {
            return Utils.isValidToStartOccasionByDate(occasionDate);
        }
        return (resource.enrollmentRequiredMetadata?.occasion_dates?.filter((occ) => !occ.disabled) ?? []).some(
            (occasion) => Utils.isValidToStartOccasionByDate(occasion)
        );
    }
    private isBetweenEnrollmentPeriodAndOccasionPeriod(resource: Resource, occasionDate?: OccasionDate): boolean {
        if (occasionDate) {
            return Utils.isBetweenEnrollmentPeriodAndOccasionPeriod(occasionDate);
        }
        return resource.enrollmentRequiredMetadata?.occasion_dates
            ?.filter((occ) => !occ.disabled)
            ?.some((occasion) => Utils.isBetweenEnrollmentPeriodAndOccasionPeriod(occasion));
    }
    private isOccasionPeriodPassed(resource: Resource, occasionDate?: OccasionDate): boolean {
        if (occasionDate) {
            return Utils.isOccasionPeriodPassed(occasionDate);
        }
        return resource.enrollmentRequiredMetadata?.occasion_dates
            ?.filter((occ) => !occ.disabled)
            ?.some((occasion) => Utils.isOccasionPeriodPassed(occasion));
    }

    private createPaymentNeededSpec(): PlayableResourceSpec {
        return {
            isAllowed: false,
            label: $localize`Pay`,
            icon: 'credit_card',
            ...SPEC_STYLES.paymentNeeded,
            checkType: ResourceCheckKeys.PRICE,
        };
    }
    private getBlockedOccasionSpec(resource: Resource, occasionDate?: OccasionDate): PlayableResourceSpec {
        if (occasionDate) {
            if (Utils.isEnrollmentPeriodPassed(occasionDate)) {
                return {
                    isAllowed: false,
                    label: $localize`Passed`,
                    icon: 'block',
                    ...SPEC_STYLES.closed,
                    desc: $localize`The enrollment period has passed for this occasion`,
                    checkType: ResourceCheckKeys.ENROLL,
                    shouldCheckPrice: true,
                };
            } else {
                const nearestApplicationDateLabel = this.getNearestApplicationDateLabel(resource, occasionDate);
                return {
                    isAllowed: false,
                    label: $localize`Soon`,
                    icon: 'schedule',
                    ...SPEC_STYLES.soon,
                    desc: $localize`The resource application period is currently not available, But it's coming soon ${
                        nearestApplicationDateLabel ? `on ${nearestApplicationDateLabel}` : ''
                    }. Stay tuned!`,
                    checkType: ResourceCheckKeys.ENROLL,
                    shouldCheckPrice: true,
                };
            }
        }
        const occasions = resource.enrollmentRequiredMetadata?.occasion_dates?.filter((occ) => !occ.disabled) ?? [];
        const hasPassedAllOccasions = occasions.every((occasion) => Utils.isEnrollmentPeriodPassed(occasion));
        const nearestApplicationDateLabel = this.getNearestApplicationDateLabel(resource, occasionDate);
        return hasPassedAllOccasions
            ? {
                  isAllowed: false,
                  label: $localize`Passed`,
                  icon: 'block',
                  ...SPEC_STYLES.closed,
                  desc: $localize`The enrollment period has passed for this resource`,
                  checkType: ResourceCheckKeys.ENROLL,
                  shouldCheckPrice: true,
              }
            : {
                  isAllowed: false,
                  label: $localize`Soon`,
                  icon: 'schedule',
                  ...SPEC_STYLES.soon,
                  desc: $localize`The resource application period is currently not available, But it's coming soon ${
                      nearestApplicationDateLabel ? `on ${nearestApplicationDateLabel}` : ''
                  }. Stay tuned!`,
                  checkType: ResourceCheckKeys.ENROLL,
                  shouldCheckPrice: true,
              };
    }

    /** Fallback when no other check applies (e.g. no enrollment metadata). */
    private createClosedFallbackSpec(resource: Resource): PlayableResourceSpec {
        const playbackButton = buttonForPlaybackAction(resource.playbackAction);
        return {
            isAllowed: true,
            label: playbackButton.label,
            icon: playbackButton.icon,
            ...SPEC_STYLES.play,
            checkType: ResourceCheckKeys.ENROLL,
            shouldCheckPrice: true,
        };
    }
    private getNearestOccasionDateLabel(resource: Resource, occasionDate?: OccasionDate): string {
        const occasionDates = occasionDate ? [occasionDate] : resource.enrollmentRequiredMetadata?.occasion_dates ?? [];
        const nearestOccasionDate = Utils.getNearestOccasionDate(occasionDates);
        return nearestOccasionDate?.start_date ? moment(nearestOccasionDate.start_date).format('MMM D, YYYY') : '';
    }
    private getNearestApplicationDateLabel(resource: Resource, occasionDate?: OccasionDate): string {
        const occasionDates = occasionDate ? [occasionDate] : resource.enrollmentRequiredMetadata?.occasion_dates ?? [];
        const nearestApplicationDate = Utils.getNearestApplicationDate(occasionDates);
        return nearestApplicationDate?.application_start_date
            ? moment(nearestApplicationDate.application_start_date).format('MMM D, YYYY')
            : '';
    }
}

/**
 * Summary of all PlayableResourceSpec responses returned by this service.
 *
 * Section 1 – Specs WITH desc (extra explanation shown to the user)
 * - SCORM error (getScormErrorSpec)
 *   - Case: SCORM resource where parent type is SCORM and prompt_status is 'ERROR'.
 *   - UI: "Can't play SCORM", icon 'block', closed style, desc explains that the SCORM is not ready (and shows the error when available).
 *
 * - No items (getNoItemsSpec)
 *   - Case: Resource type that requires items but number_of_items is missing or zero.
 *   - UI: "Closed", icon 'block', closed style, desc explains that the resource cannot be played because it has no items.
 *
 * - Competence already taken in this recurring period (getCompetenceClosedSpec)
 *   - Case: User competence exists, is not expired, and has already been taken in the current recurring period.
 *   - UI: "Closed", icon 'block', closed style, desc tells the user they have already taken the resource and shows when they can take it again.
 *
 * - Enrolled in another occasion (getEnrollmentStatusSpec)
 *   - Case: User is enrolled in a different occasion than the currently selected one.
 *   - UI: "Closed", icon 'block', closed style, desc explains that the user is already enrolled in another occasion for this resource.
 *
 * - Approved/Enrolled but between enrollment and occasion periods (getEnrollmentStatusSpec)
 *   - Case: Approval status is approved/enrolled, and the time is between enrollment period and occasion period.
 *   - UI: "Soon", icon 'schedule', soon style, desc explains that the resource is not yet available but is coming soon (price will also be checked).
 *
 * - Approved/Enrolled but occasion period has passed (getEnrollmentStatusSpec)
 *   - Case: Approval status is approved/enrolled, but the occasion period has already passed.
 *   - UI: "Passed", icon 'block', closed style, desc explains that the resource is not currently available.
 *
 * - Occasion enrollment period passed – specific occasion (getBlockedOccasionSpec with occasionDate)
 *   - Case: A specific occasionDate is given and its enrollment period has passed.
 *   - UI: "Passed", icon 'block', closed style, desc explains that the enrollment period has passed for this occasion (price will also be checked).
 *
 * - Occasion not yet open – specific occasion (getBlockedOccasionSpec with occasionDate)
 *   - Case: A specific occasionDate is given and its enrollment period has not passed, but the user still cannot enroll or start.
 *   - UI: "Soon", icon 'schedule', soon style, desc explains that the application period is not available yet but is coming soon (price will also be checked).
 *
 * - Enrollment period passed – any occasion (getBlockedOccasionSpec without occasionDate, hasPassedOccasion = true)
 *   - Case: No specific occasionDate is provided, but at least one occasion has an enrollment period that has passed.
 *   - UI: "Passed", icon 'block', closed style, desc explains that the enrollment period has passed for this resource (price will also be checked).
 *
 * - Application period not yet open – any occasion (getBlockedOccasionSpec without occasionDate, hasPassedOccasion = false)
 *   - Case: No specific occasionDate is provided, enrollment has not passed for any occasion, but the user still cannot enroll or start.
 *   - UI: "Soon", icon 'schedule', soon style, desc explains that the application period is not yet available but is coming soon (price will also be checked).
 *
 * Section 2 – Specs WITHOUT desc (only short label/icon feedback)
 * - Opportunity continue (getOpportunitySpec)
 *   - Case: Resource has an opportunity_id.
 *   - UI: "Continue", icon 'double_arrow', play style, lets the user continue their opportunity.
 *
 * - Approved and ready to play (getEnrollmentStatusSpec inside approved/enrolled when canStart is true)
 *   - Case: Approval status equals approved/enrolled and the occasion can start now.
 *   - UI: "Play", icon 'play_circle', play style.
 *
 * - Approved status only (getEnrollmentStatusSpec – ENROLLMENT_APPROVAL.APPROVED case)
 *   - Case: Enrollment approval_status is approved, but not currently in a playable window.
 *   - UI: "Approved", icon 'task_alt', approved style.
 *
 * - Pending approval (getEnrollmentStatusSpec – ENROLLMENT_APPROVAL.PENDING case)
 *   - Case: Enrollment approval_status is pending.
 *   - UI: "Pending", icon 'pending_actions', pending style.
 *
 * - Waiting/Enrolled but not yet playable (getEnrollmentStatusSpec – WAITING/ENROLLED case)
 *   - Case: Enrollment approval_status is waiting or enrolled, but the user cannot start yet.
 *   - UI: "Enrolled", icon 'pending_actions', pending style.
 *
 * - Rejected enrollment (getEnrollmentStatusSpec – ENROLLMENT_APPROVAL.REJECTED case)
 *   - Case: Enrollment approval_status is rejected.
 *   - UI: "Rejected", icon 'block', paymentNeeded style.
 *
 * - Always-open occasion – directly playable (getOpenAlwaysOrOccasionSpec with occasion_always_open)
 *   - Case: Enrollment-required metadata marks the resource as always open and there is no existing enrollment-required status.
 *   - UI: "Play", icon 'play_circle', play style, price will also be checked.
 *
 * - Occasion can be enrolled (getOpenAlwaysOrOccasionSpec when canEnroll is true)
 *   - Case: User can enroll in an occasion (seats and dates are valid).
 *   - UI: "Enroll", icon 'app_registration', play style, price will also be checked.
 *
 * - Occasion can be started (getOpenAlwaysOrOccasionSpec when canStart is true)
 *   - Case: User can start an occasion (start date conditions met).
 *   - UI: "Play", icon 'play_circle', play style, price will also be checked.
 *
 * - Payment needed (createPaymentNeededSpec)
 *   - Case: Resource requires payment (non-sitelicense customer, priced resource, not yet purchased).
 *   - UI: "Payment Needed", icon 'credit_card', paymentNeeded style.
 *
 * - Closed fallback play/repeat (createClosedFallbackSpec)
 *   - Case: No specific enrollment or occasion rule applies (fallback when checks return null).
 *   - UI: "Start" when playbackAction is missing, "Play" when it is 'start', otherwise "Repeat". Price will also be checked.
 */
